import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { listAppointments, createReportFromAppointment } from '../services/api';

function getStartOfDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function getEndOfDay(date) {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

function getEndOfWeek(date) {
  const d = new Date(date);
  const daysUntilSunday = 7 - d.getDay();
  d.setDate(d.getDate() + daysUntilSunday);
  d.setHours(23, 59, 59, 999);
  return d;
}

function formatTime(isoString) {
  return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function mapsUrl(address) {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [weekAppointments, setWeekAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);
  const [creatingReport, setCreatingReport] = useState(null);

  // Day/week boundaries, computed once per mount.
  const range = useMemo(() => {
    const now = new Date();
    return {
      todayStart: getStartOfDay(now).toISOString(),
      todayEndMs: getEndOfDay(now).getTime(),
      weekEnd: getEndOfWeek(now).toISOString(),
    };
  }, []);

  // A single request covers both lists: the week range already starts today, so
  // "today" is just a slice of it. Aborted on unmount so leaving the page can't
  // leave a dangling request (Firefox logs those as NS_BINDING_ABORTED).
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);

    listAppointments(
      { date_from: range.todayStart, date_to: range.weekEnd },
      { signal: controller.signal },
    )
      .then((week) => {
        setWeekAppointments(week || []);
        setLoading(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;  // unmounted — not an error
        setLoadError(true);
        setLoading(false);
      });

    return () => controller.abort();
  }, [range, reloadTick]);

  const todayAppointments = useMemo(
    () => weekAppointments.filter((a) => new Date(a.scheduled_at).getTime() <= range.todayEndMs),
    [weekAppointments, range.todayEndMs],
  );

  const handleCreateReport = async (appointment) => {
    setCreatingReport(appointment.id);
    try {
      const report = await createReportFromAppointment(appointment.id);
      // Update local state (the "today" list is derived from this one)
      setWeekAppointments((prev) =>
        prev.map((a) =>
          a.id === appointment.id ? { ...a, report_id: report.id, status: 'completed' } : a
        )
      );
      navigate(`/reports/${report.id}`);
    } catch {
      // error handled silently
    } finally {
      setCreatingReport(null);
    }
  };

  const pendingReports = weekAppointments.filter(
    (a) => a.status === 'completed' && !a.report_id
  ).length;

  if (loading) {
    return <p style={{ textAlign: 'center', paddingTop: 40 }}>{t('auth.loading')}</p>;
  }

  const statusBadge = (status) => {
    const map = {
      scheduled: 'badge-draft',
      completed: 'badge-completed',
      cancelled: 'badge-cancelled',
    };
    const label = t(`appointments.${status}`);
    return <span className={`badge ${map[status] || 'badge-draft'}`}>{label}</span>;
  };

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1 className="page-title">{t('dashboard.title')}</h1>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/appointments')}
        >
          + {t('appointments.create')}
        </button>
      </div>

      {loadError && (
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', borderLeft: '3px solid var(--color-danger)' }}>
          <span style={{ fontSize: 14 }}>{t('errors.generic')}</span>
          <button className="btn btn-secondary btn-sm" onClick={() => setReloadTick((n) => n + 1)}>
            {t('dashboard.retry')}
          </button>
        </div>
      )}

      {/* Stats cards */}
      <div className="dashboard-stats">
        <div className="card dashboard-stat-card">
          <div className="dashboard-stat-number">{todayAppointments.length}</div>
          <div className="dashboard-stat-label">{t('dashboard.todayCount')}</div>
        </div>
        <div className="card dashboard-stat-card">
          <div className="dashboard-stat-number">{weekAppointments.length}</div>
          <div className="dashboard-stat-label">{t('dashboard.weekCount')}</div>
        </div>
        <div className="card dashboard-stat-card">
          <div className="dashboard-stat-number">{pendingReports}</div>
          <div className="dashboard-stat-label">{t('dashboard.pendingReports')}</div>
        </div>
      </div>

      {/* Today's appointments */}
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 16, marginTop: 32 }}>
        {t('dashboard.todayAppointments')}
      </h2>

      {todayAppointments.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-state-icon">&#128197;</div>
          <p>{t('dashboard.noAppointmentsToday')}</p>
        </div>
      ) : (
        <div className="dashboard-appointments">
          {todayAppointments.map((appt) => (
            <div className="card dashboard-appointment-card" key={appt.id}>
              <div className="dashboard-appointment-time">
                {formatTime(appt.scheduled_at)}
              </div>
              <div className="dashboard-appointment-info">
                <div className="dashboard-appointment-title">{appt.title}</div>
                <div className="dashboard-appointment-customer">
                  {appt.contact_name || appt.customer?.name}
                  {appt.requesting_company && (
                    <span style={{ color: 'var(--color-text-light)', marginLeft: 8 }}>
                      ({appt.requesting_company.name})
                    </span>
                  )}
                </div>
                {appt.address && (
                  <a
                    href={mapsUrl(appt.address)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dashboard-appointment-address"
                    onClick={(e) => e.stopPropagation()}
                  >
                    &#128205; {appt.address}
                  </a>
                )}
              </div>
              <div className="dashboard-appointment-actions">
                {statusBadge(appt.status)}
                {appt.report_id ? (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => navigate(`/reports/${appt.report_id}`)}
                  >
                    {t('appointments.reportExists')}
                  </button>
                ) : (
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => handleCreateReport(appt)}
                    disabled={creatingReport === appt.id}
                  >
                    {creatingReport === appt.id
                      ? t('auth.loading')
                      : t('dashboard.createReport')}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
